import { useEffect } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { SquarePen } from 'lucide-react';
import ToolBar from '@/components/ToolBar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { displayError } from '@/lib/error';
import { convertISODateToMMDDYYYY } from '@/lib/utils';
import { isAdminOrSupervisor, isDueDiligenceTeamMember } from '@/services/userService';
import { useUserStore } from '@/stores/user';
import { useDDIWorkOrderDetail, useDdiSearchResult } from '@/services/dueDiligenceService';
import { SSN_SEARCH_DDI_TYPE } from '@/utils/constant';

const ReadOnlyField = ({ label, value }: { label: string; value: string }) => (
  <div className="space-y-2 flex flex-col gap-1">
    <Label className="flex items-center gap-1 text-sm font-medium">{label}</Label>
    <Input value={value} disabled className="bg-white" />
  </div>
);

export const DDISearchResultView = () => {
  const { id: workOrderId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const loggedInUser = useUserStore((state) => state.user);

  const { data: workOrder, isFetching: isFetchingWorkOrder, isError: isErrorWorkOrder } =
    useDDIWorkOrderDetail(workOrderId ?? null);
  const {
    data: searchResult,
    isFetching: isFetchingSearchResult,
    isError: isErrorSearchResult,
    error: searchResultError,
  } = useDdiSearchResult(workOrderId ?? null);

  const canViewSearchResult = isDueDiligenceTeamMember(loggedInUser) || isAdminOrSupervisor(loggedInUser);

  const handleBack = () => navigate('/due-diligence');

  useEffect(() => {
    if (isErrorSearchResult && searchResultError) {
      displayError(searchResultError, 5000);
    }
  }, [isErrorSearchResult, searchResultError]);

  if (!canViewSearchResult) {
    return <Navigate to="/not-found" />;
  }

  if (isFetchingWorkOrder || isFetchingSearchResult) {
    return <div className="flex items-center justify-center h-64 text-placeholder text-sm">Loading…</div>;
  }

  if (isErrorWorkOrder || isErrorSearchResult || !workOrder) {
    return (
      <div className="flex items-center justify-center h-64 text-error text-sm">
        Failed to load search result. Please go back and try again.
      </div>
    );
  }

  if (workOrder.clientDetails.ddiType !== SSN_SEARCH_DDI_TYPE) {
    return <Navigate to="/due-diligence" />;
  }

  if (!searchResult) {
    return (
      <div className="flex items-center justify-center h-64 text-placeholder text-sm">No search result found.</div>
    );
  }

  return (
    <div className="w-full scrollbar-hide flex flex-col">
      <div className="w-full flex flex-row justify-between items-start">
        <ToolBar
          title={`W/O: ${workOrder.commonDetails.workOrderNumber} - Search Results`}
          secondaryTitle={<span className="text-sm text-placeholder">SSN Search</span>}
        />
        <Button
          variant="outline"
          className="h-[36px] w-[36px] p-0 flex items-center justify-center border-primary text-primary hover:bg-[#EDF5FF]"
          aria-label="Edit search result"
          onClick={() => navigate(`/due-diligence/${workOrderId}/search-results/edit`)}
        >
          <SquarePen className="h-4 w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ReadOnlyField label="Person for Inquiry" value={searchResult.personToInquire ?? '-'} />
        <ReadOnlyField label="Date" value={convertISODateToMMDDYYYY(searchResult.date)} />
        <ReadOnlyField label="SSN" value={searchResult.ssn ?? '-'} />
        <ReadOnlyField label="DOB" value={searchResult.dob ? convertISODateToMMDDYYYY(searchResult.dob) : '-'} />
        <div className="sm:col-span-2">
          <ReadOnlyField label="Notes" value={searchResult.notes ?? '-'} />
        </div>
        <ReadOnlyField label="Searcher" value={searchResult.searcher?.name ?? '-'} />
      </div>

      <div className="flex justify-end mt-4 mb-8 w-full gap-2">
        <Button
          variant="outline"
          className="text-primary py-2 px-4 rounded-md border-primary flex justify-center w-[125px] h-[48px] hover:bg-[#EDF5FF]"
          onClick={handleBack}
        >
          Back
        </Button>
      </div>
    </div>
  );
};

export default DDISearchResultView;
